// context/AuthContext.tsx
import { Center, Loader } from "@mantine/core";
import { getCart } from "api/cart.service";
import { authApi, safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useCartStore } from "stores/useCartStore";
import { useProductStore } from "stores/useProductStore";
import { useSessionStore } from "stores/userSessionStore";
import { User } from "types/interface.types";

interface AuthContextType {
  user: User | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

// How often to ping the backend to confirm the session is still valid while
// the SPA stays open. Without this, a session that dies server-side (expiry,
// invalidation, etc.) is only ever detected on the next full page load, or
// when some other API call happens to 401 first.
const VERIFY_SESSION_INTERVAL_MS = 5 * 60 * 1000;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { isLoggedIn, setSessionVerified, user } = useSessionStore();

  const [isReady, setIsReady] = useState(false);

  // If you have a different role for customer, adjust the condition accordingly
  // For example, if you have a separate Customer role, use that instead of UserRole.Customer
  // If you want to include both Customer and Administrator as customers, you can modify
  // the condition to include both roles, like this:
  useEffect(() => {
    if (!isLoggedIn) {
      setIsReady(true);
      return;
    }

    const verifySession = async () => {
      try {
        const response = await authApi.get("/users/verifySession");
        if (response.data?.user === null) {
          return;
        }

        if (response.data?.user) {
          const currentUser = response.data?.user?.user;
          useSessionStore.getState().setUser(currentUser);

          const cartRes = await safeApiCall(getCart());
          if (cartRes) {
            useCartStore.getState().setCart(cartRes.data || []);
          }

          useProductStore.getState().syncModifiedProducts();
          useProductStore.getState().syncBackorders();
        } else {
          if (isLoggedIn) useCartStore.getState().clearCart();
          useSessionStore.getState().clearUser();
        }
      } catch (error: any) {
        // No response = network failure / browser timeout (e.g. device asleep,
        // tab frozen). That says nothing about the session, so keep the user
        // logged in and let the next check retry.
        if (!error?.response) return;

        logError(error);

        if (isLoggedIn) useCartStore.getState().clearCart();
        useSessionStore.getState().clearUser();
      } finally {
        lastVerifiedAt = Date.now();
        setSessionVerified();
        setIsReady(true);
      }
    };

    let lastVerifiedAt = 0;

    verifySession();

    // Don't poll from hidden tabs - requests there are the ones that stall and
    // time out. Catch up as soon as the tab becomes visible again instead.
    const intervalId = window.setInterval(() => {
      if (!document.hidden) verifySession();
    }, VERIFY_SESSION_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (
        !document.hidden &&
        Date.now() - lastVerifiedAt >= VERIFY_SESSION_INTERVAL_MS
      ) {
        verifySession();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user }}>
      {isReady ? (
        children
      ) : (
        <Center style={{ height: "100vh" }}>
          <Loader size="lg" />
        </Center>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
